/**
 * The ten promo-area asset types, in the legacy tab order.
 *
 * `labelKey` is an i18n key — the module stays pure, so the consuming component
 * resolves it with `$t`. `toggleValue` is the 1..10 number the legacy
 * `toggleSwitchValue(type)` map on `assets.vue` uses; it deliberately does NOT
 * follow the tab order (allArticles is 8, socialMediaPosts is 7, …).
 */
export const ASSET_TYPES = [
  {
    value: 'swipes',
    labelKey: 'frontend_resources_swipes',
    collections: ['swipes'],
    enabledKey: 'swipes_enabled',
    toggleValue: 1,
    guidance:
      'Email swipes your affiliates copy and send. Use Insert to drop in their name and link.',
  },
  {
    value: 'blogReviews',
    labelKey: 'frontend_resources_blog_reviews',
    collections: ['blog_reviews'],
    enabledKey: 'blog_reviews_enabled',
    toggleValue: 2,
    guidance: 'Ready-made review posts affiliates can publish on their own blog.',
  },
  {
    value: 'allArticles',
    labelKey: 'frontend_resources_blog_articles',
    collections: ['press_releases', 'articles'],
    enabledKey: 'press_releases_enabled',
    toggleValue: 8,
    guidance: 'Press releases and long-form articles affiliates can republish.',
  },
  {
    value: 'banners',
    labelKey: 'frontend_resources_banners',
    collections: ['banners'],
    enabledKey: 'banners_enabled',
    toggleValue: 3,
    guidance: 'Banner images in the sizes affiliates need for their sites and ads.',
  },
  {
    value: 'thankyouPageAds',
    labelKey: 'frontend_resources_thankyou',
    collections: ['thankyou_page_ads'],
    enabledKey: 'thankyou_page_ads_enabled',
    toggleValue: 4,
    guidance: 'Ads affiliates place on their thank-you pages after a sale.',
  },
  {
    value: 'logoutPageAds',
    labelKey: 'frontend_resources_logout_page',
    collections: ['logout_page_ads'],
    enabledKey: 'logout_page_ads_enabled',
    toggleValue: 5,
    guidance: 'Ads affiliates place on the logout page of their members area.',
  },
  {
    value: 'socialMediaPosts',
    labelKey: 'frontend_resources_social_media',
    collections: [
      'social_media_posts.facebook',
      'social_media_posts.twitter',
      'social_media_posts.linkedin',
    ],
    enabledKey: 'social_media_posts_enabled',
    toggleValue: 7,
    guidance: 'Short posts affiliates can share on Facebook, X and LinkedIn.',
  },
  {
    value: 'videoReviews',
    labelKey: 'frontend_resources_video_reviews',
    collections: ['video_reviews'],
    enabledKey: 'video_reviews_enabled',
    toggleValue: 9,
    guidance: 'Video reviews affiliates can embed or link to, with a short description.',
  },
  {
    value: 'facebookAds',
    labelKey: 'frontend_resources_facebook_ads',
    collections: ['facebook_ads'],
    enabledKey: 'facebook_ads_enabled',
    toggleValue: 6,
    guidance: 'Facebook ad copy, targeting interests and creatives affiliates can run.',
  },
  {
    value: 'signatures',
    labelKey: 'frontend_resources_signatures',
    collections: ['signatures.email', 'signatures.forum'],
    enabledKey: 'signatures_enabled',
    toggleValue: 10,
    guidance: 'Email and forum signatures affiliates can paste under every message.',
  },
];

const get = (obj, path) =>
  path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

/**
 * Build the sidebar summary for the ten asset types.
 *
 * `label` is the i18n key; resolve it with `$t` in the component.
 */
export function summariseAssetTypes(funnelResource = {}, errorTabValues = []) {
  const resource = funnelResource || {};
  const errors = errorTabValues || [];

  return ASSET_TYPES.map((type) => ({
    value: type.value,
    label: type.labelKey,
    count: type.collections.reduce((total, path) => {
      const collection = get(resource, path);
      return total + (Array.isArray(collection) ? collection.length : 0);
    }, 0),
    enabled: !!resource[type.enabledKey],
    error: errors.indexOf(type.value) !== -1,
  }));
}

export default ASSET_TYPES;
